<!--
  Asking a connected agent about lines of a subject without a conversation
  (a compare): the Files view's Ask agent opens it on the selected lines.
  It is the review thread's agent panel (RelayThread) about those lines: a
  range task with no thread (logic/rangeAsk.ts), offered to the agents whose
  grant on the subject covers `files`, under the lines' own topic, so the
  answers and a reply box stay with them while it is open.
-->
<script lang="ts">
  import RelayThread, { type RelayAbout, type RelayPage } from './RelayThread.svelte';
  import { RANGE_NEEDS } from '../api/relayTask';
  import { RANGE_REQUEST, rangeTask, rangeTopic, type AskedLines } from '../logic/rangeAsk';

  interface Props {
    page: RelayPage;
    lines: AskedLines;
    /** Whose repository the file is read from. */
    owner: string;
    repo: string;
    onclose: () => void;
  }
  let { page, lines, owner, repo, onclose }: Props = $props();
  const uid = $props.id();

  const about: RelayAbout = $derived({
    topic: rangeTopic(page.subject, lines),
    task: (instruction: string) => {
      if (!page.head) throw new Error(`This ${page.noun} has not loaded yet. Try again in a moment.`);
      return rangeTask(page.subject, page.head, lines, instruction, owner, repo);
    },
    needs: RANGE_NEEDS,
    carries: 'the file and lines asked about',
    canDraft: false,
    draft: async () => false,
    draftTitle: '',
    noun: 'question',
    asksHere: true,
    request: RANGE_REQUEST,
  });
  const where = $derived(`${lines.side === 'old' ? 'old' : 'new'} line${lines.start === lines.end ? ` ${lines.start}` : `s ${lines.start}–${lines.end}`}`);
</script>

<aside class="range-ask" aria-labelledby="{uid}-h">
  <div class="range-ask-head">
    <h3 id="{uid}-h">Ask an agent <span class="muted">about <code>{lines.path}</code>, {where}</span></h3>
    <button class="btn small" aria-label="Close" title="Close" onclick={onclose}>×</button>
  </div>
  {#key about.topic}<RelayThread {page} {about} />{/key}
</aside>

<style>
  .range-ask {
    position: fixed;
    right: 16px;
    bottom: 72px;
    z-index: 5;
    width: min(460px, calc(100vw - 32px));
    max-height: 60vh;
    overflow: auto;
    padding: 12px 14px;
    border: 1px solid var(--line);
    border-radius: 10px;
    background: var(--surface);
    box-shadow: var(--shadow);
  }
  .range-ask-head {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    justify-content: space-between;
  }
  .range-ask-head h3 {
    margin: 0;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
</style>
